// 読み込み待ちの表示 (11-03・作者案＝月のくるくる・常夜燈・docs/research/2026-11-03-loading-indicator.md)。
//   配布版の今＝絵を待つ間は黒の画面だけ (src/battle_asset_gate.ts の2か所: 会話場面の頭・戦いへ抜ける暗転の延長)。
//   止まって見えるので「待っている」と分かる物を、画面の右下に小さく置く。配布版は常夜燈・灯だけで粒なし (11-02 作者の答え)。開発版の ?loading=moon で月、?loading=none で黒だけ。
//   FX の決まり (docs/rules/art-style.md・FX は材質と本体の色に合わせる): コードで描いた線・輪・ネオンにしない。
//     月＝線の輪や回る弧ではなく、塗りの三日月 (光の面) が向きを変えながら回る。影の側にうっすら地球照 (月の暗い側) を残す。
//     色＝ボスの幕 HP と同じ寒色の銀白 (design-system §4.3 の #c2cede 系)・縁の光は淡い。
//   常夜燈は会話場面と同じ灯体と明かり (render.ts drawAdvGhostLight) をそのまま呼ぶ (明かりの描き方は src/lamp_light.ts)。
//   開発版の ?loadingHold=<秒> で、読み込みが速い手元でも待ちを作って見られる (src/battle_asset_gate.ts setFakeLoadMs)。
//   11-03 作者 q6「開いた直後にも出す」・memo「ボスの切り替わりでも出す」で出す場面を足した:
//     ①ページを開いた直後 = JS を読む間は index.html の起動の灯 (#boot-lamp・同じ時計で溶け込む)、タイトルの絵を待つ間は render.ts drawTitleScreen。
//     ②会話場面を通らずに戦いへ入る頭 (GameState.battleEntryWaitMs・src/battle_asset_gate.ts battleEntryStep)。

export type LoadingLook = 'none' | 'moon' | 'lamp'
let look: LoadingLook = 'lamp'
export function setLoadingLook(v: string | null): void {
  look = v === 'moon' || v === 'none' ? v : 'lamp'
}
export function loadingLook(): LoadingLook { return look }

/** 待ち始めてから出すまでの間 (すぐ読めた時に一瞬だけ出て消えるのを避ける)。 */
export const LOADING_SHOW_DELAY_MS = 250
/** ページを開いた直後、タイトルの絵を待つ上限 (ページを開いてから・JS を読む時間も含む)。過ぎたら代わりの夜の絵でタイトルを出す (11-03)。 */
export const TITLE_BOOT_WAIT_MAX_MS = 10000
/** 出てくる時の溶け込み。 */
export const LOADING_FADE_MS = 500

/** 待ち waitedMs の時の濃さ 0..1。 */
export function loadingAlpha(waitedMs: number): number {
  const t = (waitedMs - LOADING_SHOW_DELAY_MS) / LOADING_FADE_MS
  if (t <= 0) return 0
  if (t >= 1) return 1
  return t * t * (3 - 2 * t)
}

/** 画面 (960x540) 上の置き場所。会話場面の常夜燈と同じ右下の隅 (両案を同じ所で比べる)。 */
export const LOADING_X = 960 - 70
export const LOADING_Y = 540 - 78

// 月の大きさ (画面 960x540 上の半径)。
const MOON_R = 13
// 一回りの長さ。回り方は等速にせず、ゆるく速まって遅まる (機械の回転に見せない)。
const MOON_TURN_SEC = 2.2

/** 三日月の向き (ラジアン)。 tSec = 実時間。 */
export function moonAngle(tSec: number): number {
  const u = tSec / MOON_TURN_SEC
  const k = Math.floor(u)
  const f = u - k
  // 等速と smoothstep の中間 (止まりきらない・回り続けて見える)。
  const s = f * f * (3 - 2 * f)
  return (k + 0.45 * f + 0.55 * s) * Math.PI * 2
}

// 三日月の1枚を一度だけ作る (高い解像度で作り、描く時に縮める)。
let moonSprite: HTMLCanvasElement | null = null
const SPRITE_SCALE = 4
function getMoonSprite(): HTMLCanvasElement | null {
  if (moonSprite) return moonSprite
  if (typeof document === 'undefined') return null
  const R = MOON_R * SPRITE_SCALE
  const size = Math.ceil(R * 2 + 8)
  const c = document.createElement('canvas')
  c.width = size; c.height = size
  const g = c.getContext('2d')
  if (!g) return null
  const cx = size / 2, cy = size / 2
  // 光の面: 中心が明るく、縁で少し落ちる (月の縁の減光)。光の側 (左) へ寄せた明るさ。
  const body = g.createRadialGradient(cx - R * 0.35, cy - R * 0.1, 0, cx, cy, R)
  body.addColorStop(0, 'rgb(238,236,226)')
  body.addColorStop(0.55, 'rgb(214,218,222)')
  body.addColorStop(1, 'rgb(170,180,198)')
  g.fillStyle = body
  g.beginPath(); g.arc(cx, cy, R, 0, Math.PI * 2); g.fill()
  // 影の側を抜く。抜く円の縁をぼかして、明暗の境 (欠け際) を柔らかくする。
  g.globalCompositeOperation = 'destination-out'
  const ox = cx + R * 0.52, oy = cy - R * 0.08
  const cut = g.createRadialGradient(ox, oy, 0, ox, oy, R * 1.02)
  cut.addColorStop(0, 'rgba(0,0,0,1)')
  cut.addColorStop(0.86, 'rgba(0,0,0,1)')
  cut.addColorStop(1, 'rgba(0,0,0,0)')
  g.fillStyle = cut
  g.beginPath(); g.arc(ox, oy, R * 1.02, 0, Math.PI * 2); g.fill()
  moonSprite = c
  return c
}

/** 月のくるくる。 (x, y) = 月の中心。 alpha = 濃さ。 tSec = 実時間。 */
export function drawLoadingMoon(ctx: CanvasRenderingContext2D, x: number, y: number, alpha: number, tSec: number): void {
  if (alpha <= 0.01) return
  const R = MOON_R
  // ゆっくりした息 (明るさだけ・大きさは変えない)。
  const breath = 0.9 + 0.1 * Math.sin(tSec * 1.3)
  ctx.save()
  // ① 月のまわりのにじみ (面で減っていく光・縁の線を作らない)。
  ctx.globalCompositeOperation = 'lighter'
  const halo = ctx.createRadialGradient(x, y, 0, x, y, R * 3.2)
  halo.addColorStop(0, `rgba(170,186,214,${(0.16 * alpha * breath).toFixed(3)})`)
  halo.addColorStop(0.35, `rgba(120,140,180,${(0.07 * alpha * breath).toFixed(3)})`)
  halo.addColorStop(1, 'rgba(120,140,180,0)')
  ctx.fillStyle = halo
  ctx.fillRect(x - R * 3.2, y - R * 3.2, R * 6.4, R * 6.4)
  ctx.globalCompositeOperation = 'source-over'
  // ② 地球照: 月の暗い側がうっすら見える (欠けた所が抜けて輪に見えないよう、塗りで丸を残す)。
  const dark = ctx.createRadialGradient(x, y, 0, x, y, R)
  dark.addColorStop(0, `rgba(58,66,92,${(0.34 * alpha).toFixed(3)})`)
  dark.addColorStop(0.85, `rgba(44,50,74,${(0.28 * alpha).toFixed(3)})`)
  dark.addColorStop(1, 'rgba(44,50,74,0)')
  ctx.fillStyle = dark
  ctx.beginPath(); ctx.arc(x, y, R, 0, Math.PI * 2); ctx.fill()
  // ③ 三日月 (光の面) が向きを変えて回る。
  const sprite = getMoonSprite()
  if (sprite) {
    ctx.translate(x, y)
    ctx.rotate(moonAngle(tSec))
    ctx.globalAlpha = alpha * (0.86 + 0.14 * breath)
    const s = sprite.width / SPRITE_SCALE
    ctx.drawImage(sprite, -s / 2, -s / 2, s, s)
  }
  ctx.restore()
}
